import {
  Text,
  Group,
  Menu,
  ActionIcon,
  Card,
  Tabs,
  Paper,
  Table,
  Badge,
  NumberInput,
  Select,
  Checkbox,
  Button,
  Modal,
  Stack,
} from "@mantine/core";
import { IconDotsVertical } from "@tabler/icons";
import ReactJson from "react-json-view";
import { useFormatter } from "../hooks/useFomatter";
import { useState } from "react";
import { useServerApi } from "../hooks/userServerApi";
import { useForceUpdate } from "@mantine/hooks";
import RemoteSelect2 from "./remoteSelect2";
import {
  IconTrash,
  IconCirclePlus,
  IconExclamationMark,
  IconCopy,
} from "@tabler/icons";
import _ from "lodash";
import { showNotification } from "@mantine/notifications";

const COMMISSION_CURRENCY = "HKD";

const formatSummaryField = (label, value) => `${label} = ${value}`;

const getYearPayCount = (payMode) => {
  switch (payMode) {
    case "MONTHLY":
      return 12;
    case "QUARTERLY":
      return 4;
    case "SEMIANNUALLY":
      return 2;
    default:
      return 1;
  }
};

const formatExchangeRateValue = (value) =>
  Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });

const CommissionSettingOverridingYearTableBasicInfo = ({
  overrideYear,
  onUpdateOverride,
  isCustomized = false,
  mode = "view",
  isFixedCommission = false,
  readonly = false,
  commissionSetting,
  allYears = [],
  currentYearIndex = 0,
  onCopyToRight,
}) => {
  const {
    year,
    rate,
    currency,
    premium,
    yearCommission,
    termCommission,
    termBaseCommission,
    termPremium,
    payMode,
  } = overrideYear;
  const [formatter] = useFormatter();
  const isForeignCurrency = currency && currency !== COMMISSION_CURRENCY;
  const exchangeRateValue =
    premium > 0 && termPremium != null
      ? _.round(termPremium / premium, 4)
      : 1;
  const yearPayCount = getYearPayCount(payMode);
  const showYearCommissionFormula =
    !isFixedCommission &&
    payMode !== "ANNUALLY" &&
    payMode !== "LUMPSUM" &&
    termCommission != null;

  // Modal state for copy confirmation
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);

  // Get years to the right of current year
  const yearsToRight = allYears.slice(currentYearIndex + 1);

  // Initialize selected years when modal opens
  const handleOpenCopyModal = () => {
    setSelectedYears(yearsToRight.map((y) => y.year));
    setCopyModalOpen(true);
  };

  // Toggle year selection
  const toggleYearSelection = (yearValue) => {
    setSelectedYears((prev) =>
      prev.includes(yearValue)
        ? prev.filter((y) => y !== yearValue)
        : [...prev, yearValue]
    );
  };

  // Handle copy confirmation
  const handleConfirmCopy = () => {
    if (selectedYears.length > 0 && onCopyToRight) {
      onCopyToRight(overrideYear, selectedYears);
      showNotification({
        title: "Success",
        message: `Settings copied from ${year.toUpperCase()} to ${selectedYears
          .map((y) => y.toUpperCase())
          .join(", ")}`,
        color: "green",
      });
    }
    setCopyModalOpen(false);
  };

  return (
    <>
      {/* Copy Confirmation Modal */}
      <Modal
        opened={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        title="Copy Commission Settings"
        size="sm"
      >
        <Stack spacing="md">
          <Text size="sm">
            Copy settings from <strong>{year?.toUpperCase()}</strong> to the
            following years:
          </Text>
          <Stack spacing="xs">
            {yearsToRight.map((y) => (
              <Checkbox
                key={y.year}
                label={y.year?.toUpperCase()}
                checked={selectedYears.includes(y.year)}
                onChange={() => toggleYearSelection(y.year)}
              />
            ))}
          </Stack>
          {yearsToRight.length === 0 && (
            <Text size="sm" color="dimmed">
              No years available to copy to (this is the last year).
            </Text>
          )}
          <Text size="xs" color="dimmed">
            Warning: This will overwrite existing commission settings for the
            selected years.
          </Text>
          <Group position="right" mt="md">
            <Button
              variant="outline"
              size="xs"
              onClick={() => setCopyModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={handleConfirmCopy}
              disabled={selectedYears.length === 0}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Paper withBorder p="xs" mb="md">
        {overrideYear && (
          <Group position="apart" align="flex-start" noWrap>
            <Stack spacing={4}>
              {!isFixedCommission && termCommission != null && (
                <Text size="xs">
                  Term Commission (HKD) = {formatter.currency(premium, currency)}{" "}
                  × {formatter.percentage(rate * 100, false)}
                  {isForeignCurrency && (
                    <>
                      {" "}
                      × {formatExchangeRateValue(exchangeRateValue)}
                    </>
                  )}{" "}
                  = {formatter.currency(termCommission, COMMISSION_CURRENCY)}
                  {isForeignCurrency && termBaseCommission != null && (
                    <>
                      {" "}
                      ({formatter.currency(termBaseCommission, currency)})
                    </>
                  )}
                </Text>
              )}
              {showYearCommissionFormula && (
                <Text size="xs">
                  Year Commission (HKD) ={" "}
                  {formatter.currency(termCommission, COMMISSION_CURRENCY)} ×{" "}
                  {yearPayCount} ={" "}
                  {formatter.currency(yearCommission, COMMISSION_CURRENCY)}
                </Text>
              )}
              {isFixedCommission && (
                <Text size="xs">
                  Year Commission (HKD) ={" "}
                  {formatter.currency(
                    yearCommission -
                      commissionSetting.fixCommission.exculsiveDiscount,
                    COMMISSION_CURRENCY
                  )}
                </Text>
              )}
            </Stack>
            {mode == "edit" && (
              <Group spacing="xs">
                <Button
                  size="xs"
                  onClick={(e) => {
                    //   console.log("onUpdateOverride 1");
                    e.stopPropagation();
                    onUpdateOverride();
                  }}
                  py={0}
                >
                  Update
                </Button>
                {yearsToRight.length > 0 && (
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="light" size="sm">
                        <IconCopy size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={handleOpenCopyModal}>
                        <Text size="xs">Copy to right</Text>
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
            )}
          </Group>
        )}
      </Paper>
    </>
  );
};

const CommissionSettingOverridingYearTreeTable = ({
  tree,
  order,
  isCustomized = false,
  mode = "view",
  onUpdateOverrideInput,
  onRemoveTree,
  isFixedCommission = false,
  yearGrossRate = 0,
  currency = COMMISSION_CURRENCY,
  termBaseCommission = null,
  commissionSetting,
}) => {
  const { plan, table, treeSplitPercent } = tree;

  const [formatter] = useFormatter();
  const isForeignCurrency = currency && currency !== COMMISSION_CURRENCY;
  const treeBaseCommission =
    termBaseCommission != null
      ? _.round(termBaseCommission * (treeSplitPercent ?? 0) * 0.01, 2)
      : null;

  const totalPercentage = table?.reduce((sum, row) => sum + row.percentage, 0);
  const totalAmount = table?.reduce((sum, row) => sum + row.amount, 0);

  const totalCommissionRate = table?.reduce(
    (sum, row) => sum + row.commissionRate,
    0
  );

  const discountRow = table?.find((row) => row.index == -1);
  const correspondingRow = discountRow
    ? table?.find(
        (row) =>
          row.index != -1 &&
          (discountRow.user?._id
            ? row.user?._id == discountRow.user?._id
            : row.userGroup?._id == discountRow.userGroup?._id)
      )
    : null;

  const discountWarning =
    discountRow &&
    (!correspondingRow ||
      (correspondingRow && correspondingRow.amount + discountRow.amount < -0.01));

  const treePoolAmount = tree.commission
    ? isFixedCommission && commissionSetting?.fixCommission
      ? formatter.currency(
          commissionSetting.fixCommission.commission -
            commissionSetting.fixCommission.exculsiveDiscount,
          COMMISSION_CURRENCY
        )
      : formatter.currency(tree.commission, COMMISSION_CURRENCY)
    : "N/A";
  const treePoolDisplay =
    treePoolAmount +
    (isForeignCurrency && treeBaseCommission != null && !isFixedCommission
      ? ` (${formatter.currency(treeBaseCommission, currency)})`
      : "");

  // console.log(
  //   "discountWarning",
  //   discountRow,
  //   correspondingRow,
  //   discountWarning
  // );
  const totalAmountWarning = Math.abs(totalAmount - tree.commission) > 0.01;
  const totalPercentageWarning = tree.splitByAmount
    ? false
    : Math.abs(totalPercentage - 100) > 0.01;
  const remainRow = table?.find((row) => row.isRemainLevel);
  const negativeRemainWarning = Number(remainRow?.amount ?? 0) < -0.0001;
  return (
    <>
      {/* <ReactJson
        src={commissionSetting}
        collapsed
        name={"commissionSetting"}
        style={{ backgroundColor: "white" }}
      /> */}
      <Group position="apart" noWrap>
        <>
          <Group spacing={2} noWrap>
            {isCustomized && (
              <Badge size="xs" color="red" radius="xs">
                Customized
              </Badge>
            )}
            <Badge size="xs" color="blue" radius="xs">
              {tree?.consultant?.name}
            </Badge>
            {plan && !isCustomized && (
              <Badge size="xs" color="blue" radius="xs">
                Plan: {plan?.name}
              </Badge>
            )}
            {negativeRemainWarning && (
              <Badge size="xs" color="red" radius="xs">
                Remain level negative — try Reset Override
              </Badge>
            )}
            {totalAmountWarning && !negativeRemainWarning && (
              <Badge size="xs" color="orange" radius="xs">
                Split total mismatch
              </Badge>
            )}
            {mode == "edit" && (
              <Select
                size="xs"
                ml={10}
                value={
                  tree.splitByCommissionRate
                    ? "rate"
                    : tree.splitByAmount
                    ? "amount"
                    : "percentage"
                }
                onChange={(value) => {
                  tree.splitByCommissionRate = value === "rate";
                  tree.splitByAmount = value === "amount";
                  onUpdateOverrideInput();
                }}
                data={[
                  {
                    value: "percentage",
                    label: "By Level %",
                  },
                  { value: "rate", label: "By Commission Rate %" },
                  { value: "amount", label: "By Amount" },
                ]}
              />
              // <Checkbox
              //   ml={10}
              //   size="xs"
              //   label="By Rate"
              //   checked={tree.splitByCommissionRate}
              //   onChange={(event) => {
              //     tree.splitByCommissionRate = event.currentTarget.checked;
              //     // tree.splitByAmount = !tree.splitByCommissionRate;
              //     onUpdateOverrideInput();
              //   }}
              // />
            )}
            {/* {mode == "edit" && isFixedCommission && (
              <Checkbox
                ml={10}
                size="xs"
                label="By Amount"
                checked={tree.splitByAmount}
                onChange={(event) => {
                  tree.splitByAmount = event.currentTarget.checked;
                  // tree.splitByCommissionRate = !tree.splitByAmount;
                  onUpdateOverrideInput();
                }}
              />
            )} */}
          </Group>
        </>
        <>
          {mode == "view" && (
            <Text size="xs">
              {[
                formatSummaryField("Split", `${treeSplitPercent}%`),
                formatSummaryField("Pool", treePoolDisplay),
              ].join(" · ")}
            </Text>
          )}
          {mode == "edit" && (
            <Group noWrap>
              <Text size="xs">Tree Split: </Text>
              <NumberInput
                size="xs"
                value={treeSplitPercent}
                rightSection="%"
                rightSectionWidth={30}
                styles={{ rightSection: { pointerEvents: "none" } }}
                precision={2}
                onChange={(value) => {
                  tree.treeSplitPercent = value;
                  onUpdateOverrideInput();
                }}
              />
              <ActionIcon
                size="xs"
                onClick={() => {
                  onRemoveTree(tree);
                }}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          )}
        </>
      </Group>
      <Paper withBorder p="xs" mb="md">
        <Table size="xs">
          <thead>
            <tr>
              <th>
                <Text size="xs">#</Text>
              </th>
              <th>
                <Text size="xs">Level</Text>
              </th>
              {mode == "edit" && (
                <th>
                  <Text size="xs">Type</Text>
                </th>
              )}

              {!tree.splitByCommissionRate && (
                <th>
                  <Text size="xs">Percentage</Text>
                </th>
              )}

              {tree.splitByCommissionRate && (
                <th>
                  <Text size="xs">Commission Rate</Text>
                </th>
              )}
              <th>
                <Text size="xs">Amount</Text>
              </th>
              {mode == "edit" && (
                <th>
                  <Text size="xs">Actions</Text>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {table
              ?.sort((a, b) => b.index - a.index)
              .map((row, index) => (
                <tr key={index}>
                  <td>
                    <Text size="xs">{row.index} </Text>
                  </td>

                  <td>
                    {mode == "view" && (
                      // || row.index == -1
                      <>
                        <Text size="xs">
                          {row.levelType !== "userGroup" && row.user
                            ? `${row.user?.name} [${row.user?.staffId}]`
                            : row.userGroup
                            ? `${row.userGroup?.name} `
                            : row.levelType}
                        </Text>
                      </>
                    )}
                    {mode == "edit" &&
                      // && row.index != -1
                      // (row.levelType == "upper" ||
                      //   row.levelType == "user" ||
                      //   row.level == 0) && (
                      row.levelType !== "userGroup" && (
                        <RemoteSelect2
                          size={"xs"}
                          apiEntity="user"
                          labelField="name"
                          labelRender={(data) =>
                            `[${data.staffId}] ${data.name ?? ""} ${
                              data.cname ?? ""
                            }`
                          }
                          searchFields={["name", "staffId", "aliasName"]}
                          valueField="_id"
                          value={row.user}
                          onDataChange={(d) => {
                            row.user = d?._id ?? null;
                            onUpdateOverrideInput();
                          }}
                        />
                      )}
                    {mode == "edit" && row.levelType == "userGroup" && (
                      <RemoteSelect2
                        size={"xs"}
                        apiEntity="userGroup"
                        labelField="name"
                        valueField="_id"
                        value={row.userGroup}
                        onDataChange={(d) => {
                          // console.log(d);
                          row.userGroup = d?._id ?? null;
                          onUpdateOverrideInput();
                        }}
                      />
                    )}
                  </td>

                  {/* {mode == "edit" && row.index == -1 && (
                    <td>
                      <Text size="xs"></Text>
                    </td>
                  )} */}
                  {mode == "edit" && (
                    // && row.index != -1
                    <td>
                      {mode == "view" && <Text size="xs">{row.levelType}</Text>}
                      {mode == "edit" && (
                        <Select
                          size="xs"
                          data={["user", "userGroup"]}
                          value={row.levelType ?? "user"}
                          defaultValue={"user"}
                          onChange={(value) => {
                            row.levelType = value;
                            onUpdateOverrideInput();
                          }}
                        />
                      )}
                    </td>
                  )}

                  {!tree.splitByCommissionRate && (
                    <td>
                      {mode == "edit" &&
                        !tree.splitByAmount &&
                        row.index != -1 && (
                          <NumberInput
                            size="xs"
                            value={row.percentage}
                            rightSection="%"
                            precision={2}
                            rightSectionWidth={30}
                            styles={{ rightSection: { pointerEvents: "none" } }}
                            onChange={(value) => {
                              row.percentage = value;
                              onUpdateOverrideInput();
                            }}
                          />
                        )}
                      {mode == "edit" &&
                        tree.splitByAmount &&
                        row.index != -1 && (
                          <Text size="xs">{row.percentage?.toFixed(2)} %</Text>
                        )}
                      {mode == "view" &&
                        row.index != -1 &&
                        !tree.splitByAmount && (
                          <Text size="xs">{row.percentage?.toFixed(2)} %</Text>
                        )}
                      {row.index == -1 && (
                        <>
                          <Text
                            size="xs"
                            color={discountWarning ? "red" : "inherit"}
                          >
                            Excl Discount
                          </Text>
                        </>
                      )}
                    </td>
                  )}

                  {tree.splitByCommissionRate && mode == "view" && (
                    <td>
                      {row.index != -1 && (
                        <Text size="xs">
                          {row.commissionRate?.toFixed(2)} %
                        </Text>
                      )}
                      {row.index == -1 && (
                        <>
                          <Text
                            size="xs"
                            color={discountWarning ? "red" : "inherit"}
                          >
                            Excl Discount
                          </Text>
                        </>
                      )}
                    </td>
                  )}

                  {tree.splitByCommissionRate && mode == "edit" && (
                    <td>
                      <NumberInput
                        size="xs"
                        value={row.commissionRate}
                        rightSection="%"
                        precision={2}
                        rightSectionWidth={30}
                        styles={{ rightSection: { pointerEvents: "none" } }}
                        onChange={(value) => {
                          row.commissionRate = value;
                        }}
                      />
                    </td>
                  )}

                  <td>
                    {!tree.splitByAmount && (
                      <Text
                        size="xs"
                        color={
                          discountWarning && row.index == -1 ? "red" : "inherit"
                        }
                      >
                        {formatter.currency(row.amount, COMMISSION_CURRENCY)}
                      </Text>
                    )}
                    {mode == "view" && tree.splitByAmount && (
                      <Text
                        size="xs"
                        color={
                          discountWarning && row.index == -1 ? "red" : "inherit"
                        }
                      >
                        {formatter.currency(row.amount, COMMISSION_CURRENCY)}
                      </Text>
                    )}
                    {mode == "edit" && tree.splitByAmount && (
                      <NumberInput
                        size="xs"
                        value={row.amount}
                        precision={2}
                        onChange={(value) => {
                          // console.log("onChange splitByAmount:", value);
                          row.amount = value;
                          onUpdateOverrideInput();
                        }}
                      />
                    )}
                  </td>
                  {mode == "edit" && (
                    <td>
                      <Group position="center">
                        <ActionIcon
                          size="xs"
                          onClick={() => {
                            table.splice(index, 1);
                            onUpdateOverrideInput();
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                        <ActionIcon
                          size="xs"
                          onClick={() => {
                            table.splice(index, 0, {
                              level: index + 1,
                              levelType: "user",
                              percentage: 0,
                              amount: 0,
                            });
                            onUpdateOverrideInput();
                          }}
                        >
                          <IconCirclePlus size={16} />
                        </ActionIcon>
                      </Group>
                    </td>
                  )}
                </tr>
              ))}

            <tr>
              <td colSpan={2}>
                <Text size="xs" weight={700}>
                  Total
                </Text>
              </td>
              {mode == "edit" && <td></td>}

              {tree.splitByAmount && (
                <td>
                  <Text size="xs" weight={700}></Text>
                </td>
              )}
              {!tree.splitByCommissionRate && !tree.splitByAmount && (
                <td>
                  <Text
                    size="xs"
                    weight={700}
                    color={totalPercentageWarning ? "red" : "inherit"}
                  >
                    {totalPercentage?.toFixed(2)}%
                  </Text>
                  {totalPercentageWarning && (
                    <Text size="xs" color="red">
                      ( Diff: {Math.abs(totalPercentage - 100)?.toFixed(2)}% )
                    </Text>
                  )}
                </td>
              )}

              {tree.splitByCommissionRate && (
                <td>
                  <Text
                    size="xs"
                    weight={700}
                    color={
                      Math.abs(totalCommissionRate - yearGrossRate * 100) > 0.01
                        ? "red"
                        : "inherit"
                    }
                  >
                    {totalCommissionRate.toFixed(2)}%
                  </Text>
                  {Math.abs(totalCommissionRate - yearGrossRate * 100) >
                    0.01 && (
                    <Text size="xs" color="red">
                      ( {(yearGrossRate * 100)?.toFixed(2)}% )
                    </Text>
                  )}
                </td>
              )}
              <td>
                <Text
                  size="xs"
                  weight={700}
                  color={totalAmountWarning ? "red" : "inherit"}
                >
                  {formatter.currency(totalAmount, COMMISSION_CURRENCY)}
                </Text>
                {totalAmountWarning && (
                  <Text size="xs" color="red">
                    ( pool: {formatter.currency(tree.commission, COMMISSION_CURRENCY)} )
                  </Text>
                )}
              </td>
              {mode == "edit" && <td></td>}
            </tr>
          </tbody>
        </Table>
        {discountWarning && (
          <Text size="xs" color="red" mt="xs">
            Warning: The assigned user has insufficient commission to cover the
            exclusive discount.
          </Text>
        )}
      </Paper>
    </>
  );
};

const CommissionSettingOverridingCard = ({
  commissionSetting,
  onResetOverride,
  onCustomize,
  onUpdateOverride,
  mode = "view",
  disableUpdate = false,
  readonly = false,
}) => {
  const salesOverriding = commissionSetting?.salesOverriding;
  //   console.log("salesOverriding", salesOverriding);
  const isCustomized = commissionSetting?.isCustomizedOverriding;
  const forceUpdate = useForceUpdate();

  const onUpdateOverrideInput = (tree) => {
    // console.log("onUpdateOverrideInput", tree);
    forceUpdate();
  };

  const onRemoveTree = (tree, overrideYear) => {
    // console.log("onRemoveTree", tree._id, overrideYear._id);
    //filter with the tree._id
    const yearIndex = salesOverriding.findIndex(
      (year) => year._id === overrideYear._id
    );
    if (yearIndex >= 0) {
      salesOverriding[yearIndex].trees = salesOverriding[
        yearIndex
      ].trees.filter((t) => t._id !== tree._id);
    }
    // console.log("salesOverriding", salesOverriding);
    forceUpdate();
  };

  // Copy commission settings from source year to target years
  const onCopyToRight = (sourceYear, targetYearNames) => {
    if (!salesOverriding || !sourceYear || !targetYearNames?.length) return;

    // Deep clone the trees from source year
    const sourceTrees = _.cloneDeep(sourceYear.trees);

    // For each target year, copy the trees structure
    targetYearNames.forEach((targetYearName) => {
      const targetYearIndex = salesOverriding.findIndex(
        (y) => y.year === targetYearName
      );
      if (targetYearIndex >= 0) {
        // Create new trees with cloned data but preserve year-specific IDs
        const newTrees = sourceTrees.map((sourceTree) => {
          // Deep clone the entire tree structure
          const clonedTree = _.cloneDeep(sourceTree);

          // Remove MongoDB _id fields so new ones will be generated on save
          delete clonedTree._id;
          if (clonedTree.table) {
            clonedTree.table = clonedTree.table.map((row) => {
              const clonedRow = { ...row };
              delete clonedRow._id;
              return clonedRow;
            });
          }

          // Reset calculated amounts (will be recalculated on Update)
          clonedTree.premium = 0;
          clonedTree.commission = 0;
          if (clonedTree.table) {
            clonedTree.table = clonedTree.table.map((row) => ({
              ...row,
              amount: 0,
            }));
          }

          return clonedTree;
        });

        // Replace the trees in target year
        salesOverriding[targetYearIndex].trees = newTrees;
      }
    });

    forceUpdate();
  };

  return (
    <>
      {/* <ReactJson
        name={"salesOverriding"}
        src={salesOverriding}
        style={{ backgroundColor: "white" }}
        collapsed
      /> */}
      {/* {disableUpdate ? "disableUpdate" : "enableUpdate"} */}
      <Group position="apart" mt="md" mb="md">
        <Group></Group>
        <Menu position="bottom-end" withinPortal size="xs">
          <Menu.Target>
            <ActionIcon disabled={readonly}>
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={onResetOverride}
              py={5}
              disabled={disableUpdate}
            >
              <Text size="xs">Reset Override</Text>
            </Menu.Item>
            <Menu.Item onClick={onCustomize} py={5} disabled={disableUpdate}>
              <Text size="xs">Customize Override</Text>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
      {salesOverriding && salesOverriding.length > 0 && (
        <Card withBorder mb="md">
          <Tabs defaultValue={"yr1"}>
            <Tabs.List>
              {salesOverriding?.map((overrideYear) => (
                <Tabs.Tab value={overrideYear.year} key={overrideYear.year}>
                  <Text size="xs">{overrideYear.year?.toUpperCase()}</Text>
                </Tabs.Tab>
              ))}
            </Tabs.List>
            {salesOverriding?.map((overrideYear, yearIndex) => (
              <Tabs.Panel
                value={overrideYear.year}
                key={overrideYear.year}
                pt="xs"
              >
                <CommissionSettingOverridingYearTableBasicInfo
                  overrideYear={overrideYear}
                  isCustomized={isCustomized}
                  onUpdateOverride={onUpdateOverride}
                  mode={mode}
                  isFixedCommission={commissionSetting?.isFixedCommission}
                  commissionSetting={commissionSetting}
                  allYears={salesOverriding}
                  currentYearIndex={yearIndex}
                  onCopyToRight={onCopyToRight}
                />
                {overrideYear.trees.map((tree) => (
                  <CommissionSettingOverridingYearTreeTable
                    tree={tree}
                    order={salesOverriding.order}
                    isCustomized={isCustomized}
                    mode={mode}
                    onUpdateOverrideInput={onUpdateOverrideInput}
                    onRemoveTree={(tree) => onRemoveTree(tree, overrideYear)}
                    isFixedCommission={commissionSetting?.isFixedCommission}
                    yearGrossRate={overrideYear.rate}
                    currency={overrideYear.currency}
                    termBaseCommission={overrideYear.termBaseCommission}
                    commissionSetting={commissionSetting}
                  />
                ))}
              </Tabs.Panel>
            ))}
          </Tabs>
        </Card>
      )}
      {(!salesOverriding || salesOverriding.length == 0) && (
        <Card withBorder mb="md">
          <Text size="xs" color="dimmed">
            No Sales Overriding found
          </Text>
        </Card>
      )}
    </>
  );
};

export default CommissionSettingOverridingCard;
